import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import TopNav from "../components/TopNav";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PromoCodeInput from "../components/PromoCodeInput";
import { CreditCard, Loader2, Sparkles, BookOpen, Image as ImageIcon, Check, Tag } from "lucide-react";

const Account = () => {
  const navigate = useNavigate();
  const { profile, refreshProfile } = useAuth();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (params.get("upgraded") === "true") {
      setShowSuccess(true);
      refreshProfile();
      setParams({}, { replace: true });
    }
  }, [params, refreshProfile, setParams]);

  const handlePortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal");
      window.location.href = data.url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not open billing portal. Subscribe first.");
    } finally {
      setBusy(false);
    }
  };

  const planMap = {
    free: { label: "Free", color: "#5F5E5A" },
    starter: { label: "Starter", color: "#534AB7" },
    pro: { label: "Pro", color: "#1D9E75" },
  };
  const plan = planMap[profile?.plan] || planMap.free;

  const planExpiry = profile?.plan_expires_at ? new Date(profile.plan_expires_at) : null;
  const freeCredits = profile?.free_project_credits || 0;

  const usageItems = [
    { label: "Listing generations", icon: Sparkles, used: profile?.listings_used_this_month || 0, limit: profile?.plan === "pro" ? "Unlimited" : profile?.plan === "starter" ? 20 : 3 },
    { label: "Cover projects", icon: BookOpen, used: profile?.covers_used_this_month || 0, limit: profile?.plan === "pro" ? "Unlimited" : profile?.plan === "starter" ? 5 : 1 },
    { label: "AI image generations", icon: ImageIcon, used: profile?.ai_images_used_this_month || 0, limit: profile?.plan === "pro" ? 30 : profile?.plan === "starter" ? 10 : 1 },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <TopNav />
      <section className="max-w-[900px] mx-auto px-6 lg:px-10 py-12">
        <h1 className="text-4xl tracking-tight" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}>
          Account & billing
        </h1>
        <p className="mt-2 text-sm text-[#5F5E5A]">{profile?.email}</p>

        {showSuccess && (
          <div className="mt-6 bg-[#1D9E75]/10 border border-[#1D9E75]/40 rounded-[12px] p-4 flex items-start gap-3" data-testid="upgrade-success">
            <Check className="w-5 h-5 text-[#1D9E75] mt-0.5" />
            <div>
              <div className="text-sm font-medium text-[#2C2C2A]">Welcome to {plan.label}!</div>
              <div className="text-xs text-[#5F5E5A]">Your subscription is active. Enjoy your new credits.</div>
            </div>
          </div>
        )}

        <div className="mt-8 bg-white border border-[#D3D1C7] rounded-[12px] p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#5F5E5A]">Current plan</div>
              <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                <span
                  data-testid="account-plan-badge"
                  className="px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider"
                  style={{ backgroundColor: `${plan.color}15`, color: plan.color, border: `1px solid ${plan.color}40` }}
                >
                  {plan.label}
                </span>
                {planExpiry && (
                  <span data-testid="plan-expires-at" className="text-xs text-[#5F5E5A]">
                    Expires {planExpiry.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </span>
                )}
                {freeCredits > 0 && (
                  <span data-testid="free-credits" className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-[#1D9E75]/10 text-[#1D9E75] border border-[#1D9E75]/40 uppercase tracking-wider">
                    {freeCredits} free project credit{freeCredits === 1 ? "" : "s"}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {profile?.plan !== "pro" && (
                <button
                  onClick={() => navigate("/pricing")}
                  data-testid="upgrade-btn"
                  className="px-5 py-2.5 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489] transition-colors"
                >
                  Upgrade plan
                </button>
              )}
              {profile?.stripe_customer_id && (
                <button
                  onClick={handlePortal}
                  disabled={busy}
                  data-testid="manage-billing-btn"
                  className="px-5 py-2.5 rounded-[8px] border border-[#D3D1C7] text-sm font-medium hover:bg-[#F1EFE8] disabled:opacity-50 flex items-center gap-2"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  Manage billing
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 bg-white border border-[#D3D1C7] rounded-[12px] p-6">
          <div className="text-xs uppercase tracking-[0.2em] text-[#5F5E5A]">Usage this month</div>
          <div className="mt-5 space-y-4">
            {usageItems.map((u) => (
              <div key={u.label} className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2.5">
                  <u.icon className="w-4 h-4 text-[#534AB7]" strokeWidth={1.6} />
                  <span className="text-sm text-[#2C2C2A]">{u.label}</span>
                </div>
                <span className="text-sm tabular-nums text-[#5F5E5A]">
                  {u.used} <span className="text-[#5F5E5A]/60">/ {u.limit}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-6 bg-white border border-[#D3D1C7] rounded-[12px] p-6" data-testid="promo-form">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#5F5E5A]">
            <Tag className="w-3.5 h-3.5" /> Redeem a promo code
          </div>
          <div className="mt-4">
            <PromoCodeInput onApplied={() => refreshProfile?.()} />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Account;
